import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingActions from "@/components/FloatingActions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import Honeypot from "@/components/Honeypot";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const emptyForm = { name: "", email: "", phone: "", subject: "", message: "" };

export default function ContactPage() {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [hpWebsite, setHpWebsite] = useState(""); // honeypot — stays empty for humans

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.subject.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, subject and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        subject: form.subject.trim(),
        message: form.message.trim(),
        website: hpWebsite,
      });
      setSubmitted(true);
      setForm(emptyForm);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Something went wrong. Please try again or call us.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-ivory">
      <Navbar />
      <ContactHero />
      <ContactBody
        form={form}
        set={set}
        submit={submit}
        submitting={submitting}
        submitted={submitted}
        onReset={() => setSubmitted(false)}
        hpWebsite={hpWebsite}
        setHpWebsite={setHpWebsite}
      />
      <Footer />
      <FloatingActions />
    </div>
  );
}

/* Hero — phone as centrepiece over a muted midnight backdrop */
function ContactHero() {
  return (
    <section
      data-testid="contact-hero-section"
      className="relative bg-ivory text-charcoal pt-40 md:pt-48 pb-20 md:pb-28"
    >
      <div className="max-w-[92rem] mx-auto px-6 lg:px-12">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="sjc-eyebrow text-muted-light flex items-center gap-4"
        >
          <span className="sjc-hairline w-10" />
          The Concierge Desk
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          data-testid="contact-heading"
          className="font-display mt-8 text-4xl sm:text-5xl md:text-6xl lg:text-[80px] leading-[1.02] tracking-[-0.02em] max-w-4xl"
        >
          Reach us{" "}
          <span className="italic font-light">directly.</span>
        </motion.h1>

        {/* Prominent phone/email pair */}
        <div className="mt-16 md:mt-20 grid md:grid-cols-2 gap-12 lg:gap-20 max-w-4xl">
          <motion.a
            data-testid="contact-phone"
            href="tel:+61400000000"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="group block border-t border-charcoal/10 pt-8"
          >
            <p className="sjc-eyebrow text-muted-light">Reservations</p>
            <p className="font-display mt-4 text-4xl md:text-5xl lg:text-[54px] leading-none tracking-tight group-hover:text-gold transition-colors duration-500">
              +61 400 000 000
            </p>
            <p className="mt-4 text-sm text-muted-light italic font-light" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "16px" }}>
              Available 24 hours · every day of the year
            </p>
          </motion.a>

          <motion.a
            data-testid="contact-email"
            href="mailto:info@sydneyjames.com.au"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="group block border-t border-charcoal/10 pt-8"
          >
            <p className="sjc-eyebrow text-muted-light">Correspondence</p>
            <p className="font-display mt-4 text-3xl md:text-4xl lg:text-[42px] leading-tight tracking-tight break-all group-hover:text-gold transition-colors duration-500">
              info@sydneyjames.com.au
            </p>
            <p className="mt-4 text-sm text-muted-light italic font-light" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "16px" }}>
              We reply within 24 hours
            </p>
          </motion.a>
        </div>

        {/* Service area + hours row */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.36, ease: [0.16, 1, 0.3, 1] }}
          className="mt-16 md:mt-20 max-w-4xl grid grid-cols-1 md:grid-cols-2 gap-12"
        >
          <div data-testid="contact-service-area" className="border-t border-charcoal/10 pt-8">
            <p className="sjc-eyebrow text-muted-light">Service Area</p>
            <p className="mt-4 text-charcoal text-lg md:text-xl font-light leading-[1.6]">
              Greater Sydney · Sydney Airport · Hunter Valley · Southern Highlands · Blue Mountains
            </p>
          </div>
          <div data-testid="contact-hours" className="border-t border-charcoal/10 pt-8">
            <p className="sjc-eyebrow text-muted-light">Hours</p>
            <p className="mt-4 text-charcoal text-lg md:text-xl font-light leading-[1.6]">
              24 hours · seven days · including public holidays
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* Message form on warm ivory */
function ContactBody({ form, set, submit, submitting, submitted, onReset, hpWebsite, setHpWebsite }) {
  return (
    <section className="bg-warm-white border-t border-charcoal/10 py-24 md:py-32">
      <div className="max-w-[92rem] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-14 lg:gap-24">
        <div className="lg:col-span-5">
          <p className="sjc-eyebrow text-muted-light flex items-center gap-4">
            <span className="sjc-hairline w-8" />
            Send a message
          </p>
          <h2 className="font-display mt-6 text-4xl md:text-5xl lg:text-[52px] leading-[1.05] tracking-[-0.015em] text-charcoal">
            Something more{" "}
            <span className="italic font-light">complex?</span>
          </h2>
          <p className="mt-6 text-muted-light text-base md:text-lg font-light leading-[1.75] max-w-md">
            For anything the phone or an email can&apos;t cover — corporate accounts,
            multi-vehicle events, unusual itineraries — write to us here and a member
            of our team will be in touch.
          </p>
        </div>

        <div className="lg:col-span-7">
          {submitted ? (
            <motion.div
              data-testid="contact-success"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white border border-charcoal/10 p-10 md:p-14 text-center"
            >
              <div className="w-14 h-14 rounded-full border border-gold/40 text-gold grid place-items-center mx-auto">
                <CheckCircle2 className="w-6 h-6" strokeWidth={1.5} />
              </div>
              <h3 className="font-display mt-8 text-3xl md:text-4xl text-charcoal leading-tight tracking-tight">
                Thank you for writing to{" "}
                <span className="italic font-light">Sydney James.</span>
              </h3>
              <p className="mt-5 text-muted-light text-base leading-[1.75] font-light max-w-lg mx-auto">
                We&apos;ve received your message and will reply within 24 hours.
                For anything urgent, please call the concierge desk on{" "}
                <a href="tel:+61400000000" className="text-charcoal underline underline-offset-4 decoration-gold hover:text-gold transition-colors">
                  +61 400 000 000
                </a>.
              </p>
              <Button
                data-testid="contact-send-another-btn"
                onClick={onReset}
                className="mt-10 bg-midnight hover:bg-gold text-warm-white hover:text-midnight rounded-none px-8 py-6 text-[11px] tracking-[0.28em] uppercase font-semibold transition-all duration-500"
              >
                Send Another Message
              </Button>
            </motion.div>
          ) : (
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="bg-white border border-charcoal/10 p-6 md:p-10 space-y-6"
            >
              <Honeypot value={hpWebsite} onChange={setHpWebsite} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Full Name" required>
                  <LuxInput
                    testid="contact-name-input"
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Your name"
                  />
                </Field>
                <Field label="Email Address" required>
                  <LuxInput
                    testid="contact-email-input"
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@example.com"
                  />
                </Field>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Phone Number" optional>
                  <LuxInput
                    testid="contact-phone-input"
                    type="tel"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="+61 400 000 000"
                  />
                </Field>
                <Field label="Subject" required>
                  <LuxInput
                    testid="contact-subject-input"
                    value={form.subject}
                    onChange={set("subject")}
                    placeholder="How can we help?"
                  />
                </Field>
              </div>

              <Field label="Message" required>
                <Textarea
                  data-testid="contact-message-input"
                  rows={6}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Tell us about your trip or enquiry..."
                  className="border-charcoal/15 rounded-none focus:border-midnight focus:ring-0 resize-none"
                />
              </Field>

              <Button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={submitting}
                className="w-full bg-midnight hover:bg-gold text-warm-white hover:text-midnight rounded-none py-6 text-[11px] tracking-[0.28em] uppercase font-semibold transition-all duration-500"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-3 h-4 w-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    Send Message
                    <span className="ml-3 w-1.5 h-1.5 rounded-full bg-gold" />
                  </>
                )}
              </Button>

              <p className="text-[11px] tracking-[0.2em] uppercase text-muted-light/70 text-center font-medium">
                We aim to respond within 24 hours
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, optional, children }) {
  return (
    <div>
      <Label className="text-[11px] tracking-[0.22em] uppercase font-medium text-muted-light inline-flex items-center gap-1">
        {label}
        {required && <span className="text-gold">*</span>}
        {optional && <span className="text-muted-light/50 font-normal ml-1 normal-case tracking-normal">(optional)</span>}
      </Label>
      <div className="mt-2.5">{children}</div>
    </div>
  );
}

function LuxInput({ testid, ...props }) {
  return (
    <Input
      data-testid={testid}
      {...props}
      className="border-charcoal/15 rounded-none h-11 focus:border-midnight focus:ring-0"
    />
  );
}
